// The points leaderboard for completed configurations (+100 each, see api/points).
//
// A panel inside the Audit menu since 2026-10-19 (Kevin: "move the scoreboard into the
// audit"); it was a header button of its own before that. Read-only and best-effort: if the
// API is unreachable the panel says so and nothing else in cpq2 is affected.
//
// The ids in POINTS_EXCLUDE_USER_IDS (server-side, default 7840 — Kevin, who builds cpq2)
// are NOT SCORED: no ledger row is written for them, so they can never appear here. The
// footer tells such a caller that plainly — showing someone a board they can never join,
// with no explanation, reads as a bug.

import { useCallback, useEffect, useState } from "react";
import { getLaunchContext } from "../util/launchContext";
import {
  fetchLeaderboard,
  POINTS_PER_CONFIG,
  type Leaderboard,
} from "../util/pointsClient";

const MEDALS = ["🥇", "🥈", "🥉"];

/** The leaderboard, fetched when the panel mounts so it is current every time it is opened
 *  (someone else may have scored while this tab sat open). */
export default function ScoreboardPanel() {
  const [board, setBoard] = useState<Leaderboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const userId = getLaunchContext().userId;

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    const res = await fetchLeaderboard(userId);
    setBoard(res);
    setFailed(res === null);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = board?.leaderboard ?? [];
  const me = board?.me ?? null;

  return (
    <>
      <p className="cpq-audit-note">
        +{POINTS_PER_CONFIG} points for every configuration submitted. Each configuration counts once.
      </p>

      {loading && <p className="cpq-audit-note">Loading…</p>}
      {failed && !loading && (
        <p className="cpq-audit-note">
          Scoreboard unavailable right now — this doesn’t affect your configuration.
        </p>
      )}

      {!loading && !failed && rows.length === 0 && (
        <p className="cpq-audit-note">No points yet. Submit a configuration to get on the board.</p>
      )}

      {!loading && !failed && rows.length > 0 && (
        <div className="cpq-audit-scroll">
          <table className="cpq-table cpq-audit-table cpq-score-table">
            <thead>
              <tr>
                <th style={{ width: "3.5rem" }}>#</th>
                <th>Name</th>
                <th style={{ width: "6rem", textAlign: "right" }}>Points</th>
                <th style={{ width: "6rem", textAlign: "right" }}>Configs</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.userId} className={r.userId === userId ? "is-me" : undefined}>
                  <td>{MEDALS[r.rank - 1] ?? r.rank}</td>
                  <td>
                    {r.userName}
                    {r.userId === userId && <span className="cpq-score-you"> you</span>}
                  </td>
                  <td style={{ textAlign: "right", fontWeight: 700 }}>{r.points.toLocaleString()}</td>
                  <td style={{ textAlign: "right" }}>{r.configs}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* An excluded caller earns nothing, so they have no row and no standing. Say so
          plainly — showing them a board they can never appear on, with no explanation,
          reads as a bug. */}
      {!loading && !failed && board?.meExcluded && (
        <p className="cpq-score-mine">Your submits aren’t scored, so you don’t appear here.</p>
      )}
      {/* A scored caller who hasn't submitted yet: nudge, don't leave blank. */}
      {!loading && !failed && !board?.meExcluded && !me && rows.length > 0 && (
        <p className="cpq-score-mine">You’re not on the board yet — submit a configuration to score.</p>
      )}
    </>
  );
}
